import * as React from 'react';
import Highlight, { defaultProps } from 'prism-react-renderer';
// @ts-ignore
import Prism from 'prism-react-renderer/prism';
import theme from 'prism-react-renderer/themes/vsDark';

import { CopyIcon } from '../Icons';
import { useCodeBlock } from './CodeBlock';
import { formatLanguage } from './formatter';
import './Code.styles.scss';

//@ts-ignore
(typeof global !== 'undefined' ? global : window).Prism = Prism;
require('prismjs/components/prism-swift');
require('prismjs/components/prism-java');
require('prismjs/components/prism-csharp');
require('prismjs/components/prism-objectivec');

/**
 * @TODO - Manually create typings for prism and MDX-utils packages since they
 * do not offer TypeScript support.
 */

/**
 * A helper function for highlighting lines in a codeblock
 */
const calculateLinesToHighlight = (meta: any) => {
  const RE = /{([\d,-]+)}/;

  if (!RE.test(meta)) {
    return () => false;
  }

  const tokens: RegExpExecArray | null = RE.exec(meta);
  const lineNumbers = tokens
    ? tokens[1].split(`,`).map((v) => v.split(`-`).map((x) => parseInt(x, 10)))
    : [];
  return (index: number) => {
    const lineNumber = index + 1;
    const inRange = lineNumbers.some(([start, end]) =>
      end ? lineNumber >= start && lineNumber <= end : lineNumber === start
    );
    return inRange;
  };
};

/**
 * Custom code block component that includes a 'Copy' button, the code block language,
 * and syntax highlighting.
 */
/* @ts-ignore */
export const Code = ({ codeString, language, metastring, ...props }) => {
  const { isMultiLanguage } = useCodeBlock();
  const [isCopied, setIsCopied] = React.useState(false);
  const shouldHighlightLine = calculateLinesToHighlight(metastring);

  const CopyButton = () => (
    <button
      className="btn-copy"
      onClick={() => {
        copyToClipboard(codeString);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      }}>
      <CopyIcon />
      {isCopied ? 'Copied' : 'Copy'}
    </button>
  );

  return (
    <CodePanelWrapper language={language}>
      <Highlight
        {...defaultProps}
        code={codeString}
        language={language}
        theme={theme}
        {...props}>
        {({ className, style, tokens, getLineProps, getTokenProps }) => {
          return (
            <div className="highlight" data-language={language}>
              <div className="code-wrapper">
                <div
                  className="code-utility flex align-center"
                  style={{
                    justifyContent: isMultiLanguage
                      ? 'flex-end'
                      : 'space-between',
                  }}>
                  {!isMultiLanguage ? (
                    <div className="language">{formatLanguage(language)}</div>
                  ) : null}
                  <CopyButton />
                </div>

                <pre className={className} style={style}>
                  {tokens.map((line, i) => {
                    const lineProps = getLineProps({ line, key: i });
                    if (shouldHighlightLine(i)) {
                      lineProps.className = `${lineProps.className} highlight-line`;
                    }
                    return (
                      <div {...lineProps}>
                        <span className="line-number-style">{i + 1}</span>
                        {line.map((token, key) => (
                          <span {...getTokenProps({ token, key })} />
                        ))}
                      </div>
                    );
                  })}
                </pre>
              </div>
            </div>
          );
        }}
      </Highlight>
    </CodePanelWrapper>
  );
};

interface CodePanelWrapperProps {
  language: string;
  children: React.ReactNode;
}

export const CodePanelWrapper = ({
  language,
  children,
}: CodePanelWrapperProps) => {
  const { isMultiLanguage, registerLanguage, active, languages } =
    useCodeBlock();
  const isActive = !languages.length || active === language;

  React.useEffect(() => {
    registerLanguage(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isMultiLanguage ? (
    <div className={isActive ? 'language-block' : 'hidden language-block'}>
      {children}
    </div>
  ) : (
    <>{children}</>
  );
};

/**
 * A DOM helper function that copies HTML into a formatted clipboard string
 * for easy pasting.
 */
const copyToClipboard = (str: string) => {
  if (typeof window !== 'undefined') {
    const el = document.createElement('textarea');
    el.value = str;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
};
